import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ToastrService } from 'ngx-toastr';
import { Results } from '../../../models/results/results.model';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { Subject } from 'rxjs';
import { AggregationMethod } from '../../results-helper/grouped-result-table/grouped-result-table.component';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { LooseObject } from '../../../models/loose-object.model';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CashFlow } from '../../../models/results/financial/cashflow/cash-flow.model';
import Utils from '../../../util';

@Component({
  selector: 'app-baseline-cash-flow-analysis',
  templateUrl: './baseline-cash-flow-analysis.component.html',
  styleUrls: ['./baseline-cash-flow-analysis.component.scss']
})
export class BaselineCashFlowAnalysisComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {

  private dataLoadedNotifier = new Subject<boolean>();

  private onDestroyNotifier = new EventEmitter();
  private baselineResults?: Results;
  private cashFlowResults?: CashFlow;

  aggrMethod = AggregationMethod.SUM;

  exchangeRateDate?: string;
  currencyData: Unit[] = [];
  selectedCurrency?: Unit;

  mediaTypes: DropdownDisplayItem[] = [];
  selectedMediaType: Partial<['table', 'graph']> = [];

  /** Options matching the search criteria */
  filteredSearchData: ResultSelectOption[] = [];
  /** List of results */
  resultSelectOptions: ResultSelectOption[] = [];
  /** Option groups matching the search criteria */
  filteredGroupData: string[] = [];

  selectedResults: ResultSelectOption[] = [];
  searchCriteria: string = '';

  types = [];

  tableData = {};

  // graph setup variables
  graphData: GraphData[] = [];
  showGraph = false;
  yAxisLabel: string = '';
  xAxisLabel = 'years';

  private resultSelectGroups: string[] = [];

  translatedResultSelectGroups: any = {};
  translatedResultSelectOptions: any = {};

  trigger?: string;

  constructor(private projectCommService: ProjectCommunicationService,
              private toastrService: ToastrService,
              private unitService: UnitService,
              private translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setTranslations();
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(reloaded => {
        if (reloaded) {
          this.setResults();
        }
      });


    this.dataLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        if (this.currencyData) {
          // set default media
          if (!this.selectedMediaType || this.selectedMediaType.length === 0) this.selectedMediaType = ['table', 'graph'];

          // set default currency
          if (!this.selectedCurrency && this.projectCommService.getProjectInfo().defaults.currency) {
            this.selectedCurrency = this.currencyData.find(
              curr => curr.id === this.projectCommService.getProjectInfo().defaults.currency);
          }
          if (!this.selectedCurrency) {
            this.selectedCurrency = this.currencyData.find(curr => curr.id === '000000000000000000002004'); // CAD
          } else {
            this.selectedCurrency = this.currencyData.find(curr => curr.id === this.selectedCurrency.id);
          }

          if (this.selectedResults.length === 0) {
            this.initializeSelectedResults();
          }
          this.optionsChanged();
        }
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.setTranslations();
        this.optionsChanged();
        this.trigger = language;
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  initializeSelectedResults() {
    const group = 'RESULTS.DATA.CASHFLOWS.TITLE';
    const eventObj = { 'checked': true };
    this.optGroupClick(group, eventObj);
  }

  setTranslations() {
    this.xAxisLabel = this.translationService.get('RESULTS.GENERAL.YEARS');
    this.mediaTypes = [
      new DropdownDisplayItem('RESULTS.GENERAL.TABLE', 'table'),
      new DropdownDisplayItem('RESULTS.GENERAL.GRAPH', 'graph')
    ];
    Object.keys(this.translatedResultSelectGroups).forEach(group => {
      this.translatedResultSelectGroups[group] = this.translationService.get(group);
    });
    Object.keys(this.translatedResultSelectOptions).forEach(option => {
      this.translatedResultSelectOptions[option] = this.translationService.get(option);
    });
  }

  optionsChanged() {
    if (this.selectedMediaType.includes('table')) this.setTableData();
    if (this.selectedMediaType.includes('graph')) this.populateGraphData();
  }


  populateGraphData() {
    this.showGraph = false;
    this.graphData = [];
    this.yAxisLabel = this.translationService.get(this.selectedCurrency?.translationId ?? '');

    const tempGraphData: GraphData[] = [];

    if (!this.selectedResults || !this.selectedResults.length || !this.cashFlowResults) return;

    for (const opt of this.selectedResults) {
      if (!Object.keys(this.cashFlowResults).includes(opt.group)) continue;
      const cashFlowKey = opt.group as keyof CashFlow;
      const subTable = Utils.deepCopy(this.cashFlowResults[cashFlowKey]);
      this.convertValuesCurrency(subTable);
      const subKey = opt.name as keyof typeof subTable;
      let result: ResultRow = subTable[subKey];
      const vals: GraphData = {
        type: 'bar',
        x: [],
        y: [],
        name: result.label,
      };
      result.values.forEach((value, year) => {
        vals.x.push(year);
        vals.y.push(value);
      });
      tempGraphData.push(vals);
    }
    this.graphData = [...tempGraphData];
    setTimeout(() => {
      this.showGraph = false;
    });
    setTimeout(() => {
      this.graphData = [...tempGraphData];
      if (this.graphData && this.graphData.length > 0) {
        this.showGraph = true;
      }
    });
  }

  /**
   * Converts result data based on the select currency
   * @param row result data row
   */
  processExchange(row: ResultRow) {
    // Regex explanation https://regex101.com/r/usddk5/1
    const combinedUnitRegex = /(00000000000000000000(20|22)\d{2})/;
    const currencyUnitRegex = /(0000000000000000000020\d{2})/;
    const cpiUnitRegex = /(0000000000000000000022\d{2})/;

    // Check if CPI or currency and convert appropriately
    if (row.unit && row.unit.match(combinedUnitRegex) && this.selectedCurrency != null) {
      let currencyUnitId: string = '';

      if (row.unit.match(currencyUnitRegex)) {
        currencyUnitId = row.unit;
      } else if (row.unit.match(cpiUnitRegex)) {
        const cpiUnit = this.unitService.getUnitById(row.unit);
        const currencyUnit = this.unitService.getUnitByName(cpiUnit?.displayName.slice(0, 3));
        currencyUnitId = currencyUnit?.id ?? '';
      }

      // Only convert if results and selected currency are different
      if (currencyUnitId && currencyUnitId !== this.selectedCurrency.id) {
        for (let i = 0; i < row.values.length; i++) {
          row.values[i] = Unit.convertValue(row.values[i], currencyUnitId, this.selectedCurrency?.id);
        }
      }
    }
  }

  setTableData() {
    this.tableData = {};
    if (!this.selectedResults || !this.selectedResults.length ||!this.cashFlowResults) return;
    let tempTableData = this.cashFlowResults;
    tempTableData = Utils.deepCopy(tempTableData);
    for (const group of Object.keys(tempTableData) as (keyof CashFlow)[] ) {
      const subTable = tempTableData[group];
      for (const result of Object.keys(subTable)) {
        const subKey = result as keyof typeof subTable;
        if (!this.selectedResults.find(res => res.group === group && res.name === result)) {
          delete subTable[subKey];
        }
      }
    }
    this.convertValuesCurrency(tempTableData);
    this.tableData = tempTableData;
  }

  convertValuesCurrency(result: any) {
    const leafKeys = ['RESULTS.DATA.UNIT', 'RESULTS.DATA.VALUES'];
    if (leafKeys.every(leafKey => Object.keys(result).includes(leafKey))) { // results object is a leaf
      const values = result.values;
      const currency = result.unit;
      for (let i = 0; i < values.length; i++) {
        values[i] = Unit.convertValue(values[i], currency, this.selectedCurrency?.id);
      }
    } else {
      for (const subResult of Object.values(result)) {
        this.convertValuesCurrency(subResult);
      }
    }
  }

  showTable() {
    if (this.selectedMediaType.includes('table') && Object.keys(this.tableData).length > 0) {
      return true;
    }
    return false;
  }

  isShowResults() {
    if (this.selectedResults.length > 0) {
      return true;
    }
    return false;
  }


  /**
   * Filters the result select options based on the search parameters
   */
  onSearchChange(): void {
    // done in an async function call to avoid the delay caused by the function being processes
    const asyncFunction = () => {
      if (!this.resultSelectOptions) {
        return;
      }
      let search = this.searchCriteria;
      if (!search) {
        this.filteredSearchData = this.resultSelectOptions.slice();
        this.filteredGroupData = this.resultSelectGroups.slice();
        return;
      } else {
        search = search.toLowerCase();
      }

      this.filteredSearchData = this.resultSelectOptions.filter(input => {
        const translatedGroup = this.translatedResultSelectGroups[input.group];
        const translatedName = this.translatedResultSelectOptions[input.name];
        return translatedGroup.toLowerCase().indexOf(search) > -1 || translatedName.toLowerCase().indexOf(search) > -1;
      });
      this.filteredGroupData = this.resultSelectGroups.filter(
        group => {
          return this.filteredSearchData.find(x => x.group === group);
        }
      );
    };
    asyncFunction();
  }

  /**
   * Updates the selection status of the results of the specified result group
   * @param group result group clicked
   * @param event checkbox checked event
   */
  optGroupClick(group: string, event: Partial<MatCheckboxChange>): void {
    if (event.checked) {
      for (const item of this.filteredSearchData) {
        if (item.group === group) {
          if (!this.selectedResults.includes(item)) {
            this.selectedResults.push(item);
          }
        }
      }
    } else {
      for (const item of this.filteredSearchData) {
        if (item.group === group) {
          if (this.selectedResults.includes(item)) {
            this.selectedResults.splice(this.selectedResults.indexOf(item), 1);
          }
        }
      }
    }
    this.optionsChanged();
    this.selectedResults = [...this.selectedResults];
  }

  /**
   * Updates the selection status of the specified result
   * @param result result clicked
   * @param event checkbox checked event
   */
  optClick(result: ResultSelectOption): void {
    if (!this.selectedResults.includes(result)) {
      this.selectedResults.push(result);
    } else if (this.selectedResults.includes(result)) {
      this.selectedResults.splice(this.selectedResults.indexOf(result), 1);
    }
    this.optionsChanged();
    this.selectedResults = [...this.selectedResults];
  }

  /**
   * Checks if at least one option in the group is selected
   * @param group result group to check
   */
  isGroupIndeterminate(group: string): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;
    if (this.filteredSearchData && this.filteredSearchData.length > 0) {
      for (const item of this.filteredSearchData) {
        if (item.group === group) {
          if (!this.selectedResults.includes(item)) {
            atLeastOneIncluded = true;
          } else {
            allIncluded = false;
          }
        }
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }

  /**
   * Checks if all options in the group are selected
   * @param group result group to check
   */
  allGroupInputsSelected(group: string): boolean {
    for (const item of this.filteredSearchData) {
      if (item.group === group) {
        if (!this.selectedResults.includes(item)) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Checks if at least one option is selected
   */
  isCheckAllIndeterminate(): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;
    if (this.filteredSearchData && this.filteredSearchData.length > 0) {
      for (const item of this.filteredSearchData) {
        if (!this.selectedResults.includes(item)) {
          atLeastOneIncluded = true;
        } else {
          allIncluded = false;
        }
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }


  /**
   * Checks if all options are selected
   */
  allInputsSelected(): boolean {
    for (const item of this.filteredSearchData) {
      if (!this.selectedResults.includes(item)) {
        return false;
      }
    }
    return true;
  }

  inputsCheckAll($event: MatCheckboxChange): void {
    if ($event.checked) {
      if (this.filteredSearchData && this.resultSelectOptions
        && this.filteredSearchData.length === this.resultSelectOptions.length
      ) {
        this.selectedResults = this.filteredSearchData;
      } else {
        for (const item of this.filteredSearchData) {
          if (!this.selectedResults.includes(item)) {
            this.selectedResults.push(item);
          }
        }
      }
    } else {
      if (this.filteredSearchData && this.resultSelectOptions
        && this.filteredSearchData.length === this.resultSelectOptions.length
      ) {
        this.selectedResults = [];
      } else {
        for (const item of this.filteredSearchData) {
          const index = this.selectedResults.indexOf(item);
          if (index > -1) {
            this.selectedResults.splice(index, 1);
          }
        }
      }
    }
    this.optionsChanged();
    setTimeout(() => {
      this.selectedResults = [...this.selectedResults];
    });
  }


  /**
   * Get the currency exchange rates
   * @param created date of exchange rates to get
   */
  getFinExchange(created: string | null) {
    this.unitService.getCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyData = units;
        // sort rates alphabetically
        this.currencyData.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        this.dataLoadedNotifier.next(true);
      }
    );
  }


  // copied functions. maybe extract for all
  setResults() {
    this.resultSelectGroups = [];
    this.translatedResultSelectGroups = {};
    this.resultSelectOptions = [];
    this.translatedResultSelectOptions = {};
    const results = this.projectCommService.getResults();
    if (results && results.financial) {
      this.baselineResults = results;
      this.cashFlowResults = this.baselineResults.financial.cashFlow;

      const cashFlowKeys = Object.keys(this.cashFlowResults) as (keyof CashFlow)[];
      for (const groupId of cashFlowKeys) {
        if (typeof groupId !== 'string') continue;
        this.resultSelectGroups.push(groupId);
        this.translatedResultSelectGroups[groupId] = this.translationService.get(groupId);
        const subTable: LooseObject = this.cashFlowResults[groupId];
        for (const resultId of Object.keys(subTable) as (keyof typeof subTable)[]) {
          const params: LooseObject = {};
          const formatValues = subTable[resultId].formatValues ?? [];
          if (formatValues.length > 0) {
            params['%s'] = formatValues[0];
          }
          this.resultSelectOptions.push({name: resultId+'', group: groupId, meta: groupId + ', ' + resultId, params: params});
          this.translatedResultSelectOptions[resultId] = this.translationService.get(resultId+'');
        }
      }

      this.filteredSearchData = [...this.resultSelectOptions];
      this.filteredGroupData = [...this.resultSelectGroups];

      this.getFinExchange(this.baselineResults.created);
    } else {
      this.toastrService.warning(
        this.translationService.get('RESULTS.ERRORS.PLEASE_SIMULATE'),
        this.translationService.get('RESULTS.ERRORS.NO_RESULTS'));
    }
  }

}


interface ResultSelectOption {
  name: string,
  group: string,
  meta: string,
  params: LooseObject
};

interface GraphData {
  type: string
  x: number[],
  y: number[],
  name: string
}